import React, { useEffect, useContext, useState, Component }  from 'react'
import { View, StyleSheet, FlatList, Linking, TouchableOpacity, Platform } from 'react-native'
import { Button, Text, Icon, Spinner, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { getProductStatsForDays, getActiveSupplyOrders } from '../../global/Firebase/firebase'
import StyledTextInput from '../../components/StyledTextInput'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, productUnitsDictAtom, productsDictAtom, productsUpdatedAtom, storesDictAtom, suppliersDictAtom } from '../../global/AtomsProvider'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { formatRelative } from 'date-fns'
import { useColors } from '../../utils/useColors'
import { roundNumber } from '../../utils/numberUtils'
import Fuse from 'fuse.js'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
// ATL-968: render in catalog order rather than dict-insertion order.
import { getProductsList } from '../../utils/productListHelpers'
import { sumProductReportQuantities } from '../../utils/productReportQuantityTotals'
import { supplyOrderLineOnTheWay } from '../../utils/supplyOrderLine'
import {
  sumProductStockForStores,
  valueProductStockForStores,
  productRestockScore,
  countActiveStoresForProduct
} from '../../utils/productStockTotals'

export default function ProductStocksScreen({ navigation, route }) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  
  // ======== STATE ========
  const [isLoading, setIsLoading] = useState(true)  
  const [store, setStore] = useState(context.user.store?.id)
  const [combinedData, setCombinedData] = useState(null)
  const [query, setQuery] = useState('')
  const [data, setData] = useState(combinedData)
  const [activeSupplyOrders, setActiveSupplyOrders] = useState({})
  const [itemsOnTheWayDict, setItemsOnTheWayDict] = useState({})
  const [fuse, setFuse] = useState(null)
  
  // ======== ATOMS ========
  const productsDict = useAtomValue(productsDictAtom)
  const productsUpdated = useAtomValue(productsUpdatedAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const defaultProductUnit = t('pcs')
  const db = context.db

  // ======== EFFECTS ========
  // Main data loading effect
  useEffect(() => {
    getData()
  }, [store, productsUpdated.updateCount])

  // Set up Fuse search
  useEffect(() => {
    if (combinedData) {
      createCustomFuse(combinedData)
    }
  }, [store, combinedData])

  // Calculate items on the way when orders change
  useEffect(() => {
    calculateItemsOnTheWay()
  }, [activeSupplyOrders])
  
  // Refresh orders when screen is focused
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // Only fetch supply orders to avoid unnecessary product data refresh
      refreshActiveOrders();
    });

    return unsubscribe;
  }, [navigation, store]);

  // ======== DATA FETCHING ========
  // Refresh active orders without reloading all data
  const refreshActiveOrders = async () => {
    if (store === 'all') {
      // Aggregate active orders from all stores
      let allOrders = {};
      for (let storeId in storesDict) {
        const storeOrders = await getActiveSupplyOrders(db, storeId);
        // Merge orders by supplier
        for (let supplierId in storeOrders) {
          if (!allOrders[supplierId]) {
            allOrders[supplierId] = [];
          }
          allOrders[supplierId] = [...allOrders[supplierId], ...storeOrders[supplierId]];
        }
      }
      setActiveSupplyOrders(allOrders);
    } else {
      const orders = await getActiveSupplyOrders(db, store);
      setActiveSupplyOrders(orders);
    }
  };

  // Get sales data for the past week
  const getSalesData = async () => {
    // get sales from daily product stats - 7 days
    var today = new Date()
    var ago = new Date()
    ago.setDate(today.getDate() - 7)
    let newRange = {
      startDate: ago, 
      endDate: today
    }

    const stats = await getProductStatsForDays(db, newRange, store === 'all' ? null : store, false, true)
    return sumProductReportQuantities(stats)
  }

  // Main data loading function
  const getData = async () => {
    const salesData = await getSalesData()
    const suppliers = groupBySuppliers(salesData)
    let newData = identifyEmptyingShelfs(suppliers)
    let flashListProducts = newData.map(supplierData => {
      return [
        {isSupplier: true, uniqueKey: supplierData.id, ...supplierData},
        ...supplierData.products
      ]
    }).flat()
    setData(flashListProducts)
    setCombinedData(flashListProducts)

    // Log stock data for E2E testing
    const supplierItems = flashListProducts.filter(item => item.isSupplier)
    const productItems = flashListProducts.filter(item => !item.isSupplier)
    console.log('[PRODUCT_STOCKS_DATA]', JSON.stringify(sanitizeForFirestore({
      store,
      supplierCount: supplierItems.length,
      productCount: productItems.length,
      suppliers: supplierItems.map(s => ({
        name: s.name,
        id: s.id,
        productPurchaseSum: s.productPurchaseSum,
        productSaleSum: s.productSaleSum,
        productCount: s.products?.length,
      })),
      firstProducts: productItems.slice(0, 5).map(p => ({
        id: p.id,
        name: p.name,
        storeStock: p.storeStock,
        totalStoreStock: p.totalStoreStock,
        saleQuantity: p.saleQuantity,
        writeOffQuantity: p.writeOffQuantity,
        supplierID: p.supplierID,
      })),
    })))

    if (store === 'all') {
      // Create a custom fuse instance for 'all' stores mode
      createCustomFuse(flashListProducts)
      
      // Aggregate active orders from all stores
      let allOrders = {};
      for (let storeId in storesDict) {
        const storeOrders = await getActiveSupplyOrders(db, storeId);
        // Merge orders by supplier
        for (let supplierId in storeOrders) {
          if (!allOrders[supplierId]) {
            allOrders[supplierId] = [];
          }
          allOrders[supplierId] = [...allOrders[supplierId], ...storeOrders[supplierId]];
        }
      }
      setActiveSupplyOrders(allOrders);
    } else {
      const orders = await getActiveSupplyOrders(db, store);
      setActiveSupplyOrders(orders);
    }
    
    calculateItemsOnTheWay()
    setIsLoading(false)
  }

  // ======== DATA PROCESSING ========
  // Group products by suppliers
  const groupBySuppliers = (salesByProducts) => {
    var newGroupedBySuppliers = {}
    
    if (store === 'all') {
      // Handle all stores case
      getProductsList(productsDict).forEach(value => {
        // Check if the product is active in at least one store and not a service
        const isActiveInAnyStore = Object.keys(value.activeInStore || {}).some(storeId => value.activeInStore[storeId]);
        const isProductToInclude = isActiveInAnyStore && !(value.isService || (value.isComposite && !value.manufacturedBeforeSale));
        
        if (isProductToInclude) {
          // Calculate total stock across all stores
          const totalStock = sumProductStockForStores(value.storeStock, storesDict)
          
          // Create a version of the product with combined stock data
          const productWithTotalStock = {
            ...value,
            ...salesByProducts?.[value.id],
            uniqueKey: value.id,
            totalStoreStock: totalStock,
            // ATL-1377: the branch count the row's low-stock colour scales `stockThreshold` by.
            // Computed here, where the store list is in scope, because ProductRow only has props.
            activeStoreCount: countActiveStoresForProduct(value, storesDict)
          };
          
          if (newGroupedBySuppliers[value.supplierID]) {
            var newArr = [...newGroupedBySuppliers[value.supplierID].products];
            newArr.push(productWithTotalStock);
            newGroupedBySuppliers[value.supplierID].products = newArr;
            
            // ATL-1377: score the branches the organization still has, never the product
            // document's own key set — a deleted branch left in minus otherwise outranks every
            // real shortage this supplier has.
            newGroupedBySuppliers[value.supplierID].score =
              (newGroupedBySuppliers[value.supplierID].score || 0) + productRestockScore(value, storesDict);
          } else {
            newGroupedBySuppliers[value.supplierID] = {
              products: [productWithTotalStock]
            };
            
            // ATL-1377: score the branches the organization still has, never the product
            // document's own key set — a deleted branch left in minus otherwise outranks every
            // real shortage this supplier has.
            newGroupedBySuppliers[value.supplierID].score =
              (newGroupedBySuppliers[value.supplierID].score || 0) + productRestockScore(value, storesDict);
          }
        }
      });
    } else {
      // Original single store implementation
      getProductsList(productsDict).forEach(value => {
        if (value.activeInStore[store] && !(value.isService || (value.isComposite && !value.manufacturedBeforeSale))) {
          if (newGroupedBySuppliers[value.supplierID]) {
            var newArr = [...newGroupedBySuppliers[value.supplierID].products]
            newArr.push({
              ...value,
              ...salesByProducts?.[value.id],
              uniqueKey: value.id,
            })
            newGroupedBySuppliers[value.supplierID].products = newArr
            if (value.storeStock[store] < (value.stockThreshold || 1)) {
              newGroupedBySuppliers[value.supplierID].score = (newGroupedBySuppliers[value.supplierID].score || 0) + ((value.importance != null ? value.importance : 1) * ((value.stockThreshold || 1)-value.storeStock[store]))
            }
          } else {
            newGroupedBySuppliers[value.supplierID] = {
              products: [{
                ...value,
                ...salesByProducts?.[value.id],
                uniqueKey: value.id,
              }]
            } 
            if (value.storeStock[store] < (value.stockThreshold || 1)) {
              newGroupedBySuppliers[value.supplierID].score = (newGroupedBySuppliers[value.supplierID].score || 0) + ((value.importance != null ? value.importance : 1) * ((value.stockThreshold || 1)-value.storeStock[store]))
            }
          }
        }
      })
    }
    
    return newGroupedBySuppliers
  }

  // Process supplier groups and identify important products
  const identifyEmptyingShelfs = (groupedBySuppliers) => {
    var finalSuppliers = {}
    var newImportantProducts = []
    var newImportantProductsPurchaseSum = 0
    var newImportantProductsSaleSum = 0
    
    for (var supplier in groupedBySuppliers) {
      let productPurchaseSum = 0
      let productSaleSum = 0
      
      for (var product of groupedBySuppliers[supplier].products) {
        if (store === 'all') {
          // ATL-1377: value the branches the organization still has. `activeInStore` cannot
          // carry this on its own — ATL-911 only prunes that key for stores deleted AFTER it
          // shipped, and the keys orphaned before it are still marked active.
          const { purchaseAmount: productTotal, saleAmount: saleTotal } =
            valueProductStockForStores(product, storesDict);
          
          productPurchaseSum += productTotal;
          productSaleSum += saleTotal;
          
          if (product.importance > 1 && !(product.isService || (product.isComposite && !product.manufacturedBeforeSale))) {
            newImportantProducts.push({...product, uniqueKey: product.id + 'important'})
            newImportantProductsPurchaseSum += productTotal;
            newImportantProductsSaleSum += saleTotal;
          }
        } else {
          // Original single store calculation
          productPurchaseSum += (product.storeStock[store] || 0) * (product.purchasePrice || 0)
          productSaleSum += (product.storeStock[store] || 0) * (product.salePrices[store] || 0)
          
          if (product.importance > 1 && !(product.isService || (product.isComposite && !product.manufacturedBeforeSale))) {
            newImportantProducts.push({...product, uniqueKey: product.id + 'important'})
            newImportantProductsPurchaseSum += (product.storeStock[store] || 0) * (product.purchasePrice || 0)
            newImportantProductsSaleSum += (product.storeStock[store] || 0) * (product.salePrices[store] || 0)
          }
        }
      }
      
      var updatedSupplier = {...groupedBySuppliers[supplier], ...suppliersDict[supplier], productPurchaseSum: productPurchaseSum, productSaleSum: productSaleSum}
      if (updatedSupplier.active != false && !updatedSupplier.ignoreInSupplyOrders) {
        finalSuppliers[supplier] = updatedSupplier
      }
    }
    
    newImportantProducts.sort((a, b) => b.importance - a.importance)

    var newCombinedData = []
    // first show new ones - not ordered, then ordered
    for (var supplier in finalSuppliers) {
      newCombinedData.push({
        ...groupedBySuppliers[supplier],
        ...finalSuppliers[supplier],
        name: suppliersDict[supplier]?.name,
        normalizedScore: finalSuppliers[supplier].ordered ? 0 : (groupedBySuppliers[supplier]?.score || 0) / (groupedBySuppliers[supplier]?.products?.length || 1)
      })
    }   
    newCombinedData.sort((a, b) => b.normalizedScore - a.normalizedScore)
    newCombinedData.splice(0, 0, {isSupplier: true, uniqueKey: 'importantProducts', products: newImportantProducts, name: t('productStocks.importantProducts'), productPurchaseSum: newImportantProductsPurchaseSum, productSaleSum: newImportantProductsSaleSum})
    return newCombinedData
  }

  // Calculate items on the way in active orders
  const calculateItemsOnTheWay = () => {
    let newItemsOnTheWay = {}
    Object.values(activeSupplyOrders).forEach(supplierOrders => {
      supplierOrders.forEach(order => {
        order.items.forEach(orderItem => {
          // ATL-1633: stored orders hold string quantities ('240'); `0 + '240'` rendered +0240.
          // ATL-1966: a four-key line read from Supabase names its product `productId` and its
          // quantity `quantity`; read by shape, it counts instead of landing under `undefined`.
          const onTheWay = supplyOrderLineOnTheWay(orderItem)
          if (!onTheWay) return
          newItemsOnTheWay[onTheWay.productId] = (newItemsOnTheWay[onTheWay.productId] || 0) + onTheWay.quantity
        })
      })
    })
    setItemsOnTheWayDict(newItemsOnTheWay)
  }

  // ======== SEARCH FUNCTIONALITY ========
  // Create custom Fuse search instance 
  const createCustomFuse = (data) => {
    const fuseOptions = {
      keys: [
        { name: 'name', weight: 0.6 },
        { name: 'supplierName', weight: 0.2 },
        { name: 'categoryName', weight: 0.2 }
      ],
      threshold: 0.4,
      distance: 100,
    }
    
    // Filter out supplier headers and prepare search data with totalStoreStock
    const searchableData = data.filter(item => {
      // Filter out all supplier headers and the special importantProducts section
      return !item.isSupplier && !item.uniqueKey.endsWith('important');
    }).map(item => ({
      ...item,
      supplierName: suppliersDict[item.supplierID]?.name || '',
      // Include any other properties needed for search here
    }))
    
    setFuse(new Fuse(searchableData, fuseOptions))
  }

  // Handle search input and filter data
  const handleSearch = text => {
    setQuery(text)
    if (text.length === 0) {
      setData(combinedData)
      return
    }
    
    if (combinedData != null && fuse) {
      // Search using Fuse
      const searchResults = fuse.search(text)

      // Map back to original product objects
      const filteredValue = searchResults.map(result => result.item)
      
      // Group results by supplier
      const supplierGroups = {}
      filteredValue.forEach(product => {
        if (!supplierGroups[product.supplierID]) {
          supplierGroups[product.supplierID] = []
        }
        supplierGroups[product.supplierID].push(product)
      })
      
      // Add supplier headers and flatten
      const resultsWithHeaders = Object.entries(supplierGroups).map(([supplierId, products]) => {
        const supplierHeader = combinedData.find(item => 
          item.isSupplier && item.id === supplierId
        )
        
        return [
          supplierHeader || {
            isSupplier: true, 
            uniqueKey: supplierId, 
            id: supplierId,
            name: suppliersDict[supplierId]?.name || 'Unknown Supplier',
            products: products
          },
          ...products
        ]
      }).flat()
      
      setData(resultsWithHeaders)
      
    }
  }

  // ======== RENDERING FUNCTIONS ========
  // Render a supplier row
  const renderSupplierRow = (item) => {
    const supplierOrders = activeSupplyOrders[item.id] || []
    
    return (
      <View key={item.uniqueKey} style={{marginTop: 20}}>
        <View style={styles.supplierHeader}>
          <TouchableOpacity 
            onPress={item.id ? () => {
              // Use user's store as fallback when 'all' is selected
              const detailStore = store === 'all' ? context.user.store?.id : store;
              navigation.navigate('SupplierDetail', {detail: suppliersDict[item.id], preselectedStore: detailStore})
            } : null}
            style={styles.supplierTitle}
          >
            <Text category='h3'>{item.name}</Text>
          </TouchableOpacity>

          {item.id && (
            <TouchableOpacity
              onPress={() => {
                // Use user's store as fallback when 'all' is selected
                const detailStore = store === 'all' ? context.user.store?.id : store;
                navigation.navigate('SupplyOrderDetail', {
                  preselectedSupplier: suppliersDict[item.id], preselectedStore: detailStore
                })
              }}
            >
              <Icon
                name='plus-outline'
                width={24}
                height={24}
                fill={colors.info}
              />
            </TouchableOpacity>
          )}
        </View>
          
        <View style={styles.categorySubheader}>
          <Text category='c1'>{t('productStocks.purchaseSum') + ': ' + currencyFormatter.format(item.productPurchaseSum)}</Text>
          <Text category='c1'>{t('productStocks.saleSum') + ': ' + currencyFormatter.format(item.productSaleSum)}</Text>
        </View>
        
        {supplierOrders.length > 0 && (
          <View style={[styles.activeOrdersContainer, { borderLeftColor: colors.info, backgroundColor: theme['background-basic-color-2'] }]}>
            <Text category='s2' style={[styles.activeOrdersTitle, { color: colors.info }]}>
              {t('productStocks.activeOrders')}:
            </Text>
            {supplierOrders.map(order => (
              <TouchableOpacity 
                key={order.id}
                style={[styles.activeOrderRow, {borderBottomColor: theme['border-basic-color-3']}]}
                onPress={() => {
                  // When coming from 'all' stores view, make sure we use the order's specific store
                  // If no store in the order, fallback to user's store
                  if (store === 'all' && !order.store) {
                    const orderWithStore = {
                      ...order,
                      store: context.user.store?.id
                    };
                    navigation.navigate('SupplyOrderDetail', { detail: orderWithStore });
                  } else {
                    navigation.navigate('SupplyOrderDetail', { detail: order });
                  }
                }}
              >
                <Text category='p2'>
                  {formatRelative(order.date.toDate(), new Date())}
                </Text>
                <Text category='p2'>
                  {currencyFormatter.format(order.totalSum)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    )
  }

  // Render a product row
  const renderProductRow = (item, index) => {
    const itemsOnTheWay = itemsOnTheWayDict[item.id] || 0
    return (
      <ProductRow
        item={item}
        index={index}
        store={store}
        onItemPress={() => {
          // If 'all' is selected, use the user's store as a fallback for the detail view
          const detailStore = store === 'all' ? context.user.store?.id : store;
          navigation.navigate('ProductStockMovement', { product: item, store: detailStore })
        }}
        productsUpdated={productsUpdated}
        currencyFormatter={currencyFormatter}
        productUnitsDict={productUnitsDict}
        defaultProductUnit={defaultProductUnit}
        weeklySalesText={t('productStocks.weeklySales')}
        weeklyWriteOffsText={t('productStocks.weeklyWriteOffs')}
        isServiceText={t('productStocks.isService')}
        itemsOnTheWay={itemsOnTheWay}
        rowBgColor={theme['background-basic-color-1']}
        infoColor={colors.info}
      />
    )
  }

  // Main renderItem function for FlatList
  const renderItem = ({item, index}) => {
    if (item.isSupplier) {
      return renderSupplierRow(item);
    } else {
      return renderProductRow(item, index);
    }
  }

  // Render header with store chooser and search
  const header = () => (
    <View>
      {context.user.role === 'admin' && 
        <View style={{flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 3, marginBottom: 0, flexShrink: 1, marginRight: -20}}>
          <Button 
            style={{paddingHorizontal: 0, flexShrink: 1}} 
            appearance='ghost' 
            size='small' 
            onPress={() => {
              navigation.navigate('SupplyOrders')
            }} 
            accessoryLeft={(props) => (
              <Icon name='log-in-outline' {...props} style={{...props.style, marginRight: -5}}/>
            )}
          >
            {t('supplyOrders.supplyOrders')}
          </Button>
          <Button 
            style={{paddingHorizontal: 0, flexShrink: 1}} 
            appearance='ghost' 
            size='small' 
            onPress={() => {
              Linking.openURL('https://atlas-app.notion.site/15479a3a4c5941ea81283658a1a7183d')
            }} 
            accessoryLeft={(props) => (
              <Icon name='question-mark-circle-outline' {...props} style={{...props.style, marginRight: -5}}/>
            )}
          >
            {t('productStocks.productStocksInstructions')}
          </Button>
        </View>
      }
      <StoreChooserHorizontal store={store} showAll={true} onChange={(newStore) => {
        setStore(newStore)
      }} />
      <StyledTextInput autoCapitalize="none"
        autoCorrect={false}
        clearButtonMode="always"
        value={query}
        onChangeText={queryText => handleSearch(queryText)}
        placeholder={t('search')} 
        returnKeyType='done'
        style={{marginTop: 5}}
        selectTextOnFocus={true}
      />
    </View>
  )
    
  // ======== MAIN COMPONENT RETURN ========
  return (
    <FlatList
      ListHeaderComponent={header()}
      contentContainerStyle={{paddingHorizontal: 20}}
      data={data}
      renderItem={renderItem}
      keyExtractor={(item, index) => item.uniqueKey}
      keyboardDismissMode='on-drag'
      keyboardShouldPersistTaps='handled'
      ListEmptyComponent={() => (
        <View style={styles.emptyContainer}>
          {isLoading ? (
            <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
              <Spinner size="large" status="primary" />
            </View>
          ) : (
            <Text category="s1">{t('noItemsText')}</Text>
          )}
        </View>
      )}
      ListFooterComponent={() => <View style={{height: 100}}></View>}
    />
  )
}

// ======== PRODUCT ROW COMPONENT ========
class ProductRow extends Component {
  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.productsUpdated.updateCount != nextProps.productsUpdated.updateCount) {
      return true
    }
    if (this.props.item.id !== nextProps.item.id) {
      return true;
    }
    if (this.props.store !== nextProps.store) {
      return true;
    }
    if (this.props.store === 'all' && this.props.item.totalStoreStock !== nextProps.item.totalStoreStock) {
      return true;
    }
    if (this.props.item.storeStock?.[this.props.store] !== nextProps.item.storeStock?.[nextProps.store]) {
      return true;
    }
    if (this.props.item.name !== nextProps.item.name) {
      return true;
    }
    if (this.props.itemsOnTheWay !== nextProps.itemsOnTheWay) {
      return true;
    }
    return false;
  }

  render() { 
    const { item, index, store, onItemPress, productUnitsDict, defaultProductUnit, weeklySalesText, weeklyWriteOffsText, isServiceText, itemsOnTheWay, rowBgColor, infoColor } = this.props;
    
    // Determine which stock value to display based on store selection
    const stockValue = store === 'all' ? (item.totalStoreStock || 0) : (item.storeStock?.[store] || 0);
    // ATL-1377: scale the per-branch threshold by the branches the ORGANIZATION still has
    // (`activeStoreCount`, set in groupBySuppliers) — not by the product's `activeInStore` keys,
    // which still name deleted branches and would measure a full shelf against too high a bar.
    const stockThresholdStatus = store === 'all' 
      ? stockValue > (item.stockThreshold || 0) * (item.activeStoreCount || 0)
      : stockValue > (item.stockThreshold || 0);
    
    return (
      <TouchableOpacity
        style={[styles.item, {backgroundColor: rowBgColor}]}
        onPress={() => {
          onItemPress(item, index, store)
        }}
        key={item.id}
        testID={`stock-product-${index}`}
        {...(Platform.OS === 'web' ? { dataSet: { testid: `stock-product-${index}` } } : {})}
      >
        <View style={styles.itemRow}>
          <View style={{flex: 1}}>
            <View style={styles.itemTitleContainer}>
              <Text style={styles.itemTitle} category='s2'>
                {item.name}
              </Text>
              { item.isService ? 
                <Text style={styles.itemQuantity} category='s2'>
                  {isServiceText}
                </Text>
              :
                <View style={styles.quantityContainer}>
                  <Text style={styles.itemQuantity} category='s2' status={stockThresholdStatus ? 'basic' : 'danger'}>
                    {roundNumber(stockValue) + ' ' + (productUnitsDict[item.productUnit]?.shortName || defaultProductUnit)}
                  </Text>
                  {itemsOnTheWay > 0 && (
                    <Text style={[styles.onTheWayText, { color: infoColor }]} category='c1'>
                      (+{roundNumber(itemsOnTheWay)})
                    </Text>
                  )}
                </View>
              }
            </View>
            <View style={styles.itemDetailsContainer}>
              <Text style={styles.itemPrice} category='c1'>
                {weeklySalesText + ': ' + roundNumber(item.saleQuantity || 0) + ' ' + (productUnitsDict[item.productUnit]?.shortName || defaultProductUnit)}
              </Text> 
              { !item.isService && 
                <Text style={styles.itemQuantity} category='c1'>
                  {weeklyWriteOffsText + ': ' + roundNumber(item.writeOffQuantity || 0) + ' ' + (productUnitsDict[item.productUnit]?.shortName || defaultProductUnit)}
                </Text>
              }
            </View>
          </View>
        </View>
      </TouchableOpacity>
    )
  }
}

// ======== STYLES ========
const styles = StyleSheet.create({
  categorySubheader: {marginBottom: 10, marginTop: 5},
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 50,
  },
  item: {
    marginVertical: 2,
    paddingHorizontal: 14,
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    paddingVertical: 10,
  },
  itemRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  itemDetailsContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 7,
  },
  itemTitle: {
    flex: 1,
  },
  itemQuantity: {
    marginLeft: 10,
  },
  activeOrdersContainer: {
    marginBottom: 12,
    backgroundColor: '#f8f9fc',
    padding: 8,
    borderLeftWidth: 4,
  },
  activeOrdersTitle: {
    marginBottom: 4,
  },
  activeOrderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  supplierHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  supplierTitle: {
    flex: 1,
  },
  quantityContainer: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  onTheWayText: {
    marginTop: 2,
  },
})
